// GtmLoader.jsx
import { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import loadGtmScript from '../resource/loadGtmScript.js';
import env from '../resource/env.js';
import { gtm as gtmDebug } from '../resource/debug.js';
import { setTrackHeader } from '../resource/fetchOptionHeader.js';

export const GTM_DATA_LAYER = 'gtmDataLayer';

const gtmId = env.GOOGLE_TAG_MANAGER_ID;
const isClient = typeof window !== 'undefined';
const log = gtmDebug.extend('log:GtmLoader');

if (isClient) {
  window[GTM_DATA_LAYER] = window[GTM_DATA_LAYER] || [];
  log('data layer inited');
}

export const GtmLoader = ({
  clientId = null,
  userId = null,
  username = null,
}) => {
  const initGtm = useCallback(async () => {
    window[GTM_DATA_LAYER].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
    log('start load gtm');

    const loadResult = await loadGtmScript({
      gtmId,
      dataLayer: GTM_DATA_LAYER,
    });
    log('gtm loaded', { loadResult });

    return;
  }, []);

  const setGtmCookieToXTrack = useCallback(() => {
    const gtmCookie = document.cookie
      .split(';')
      .filter(s => s.startsWith(' _ga_'))
      .map(s => s.trim())
      .map(s => s.replace(/^[^a-zA-Z]*/, ''));
    log('gtm cookie', { gtmCookie });

    if (gtmCookie.length) {
      const trackObject = gtmCookie.reduce((current, cookie) => {
        const [key, value] = cookie.split('=');
        return { ...current, [key]: value };
      }, {});
      log('set gtm cookie to x-track', { gtmCookie, trackObject });
      setTrackHeader({ trackObject, shouldMergeUtmObject: true });
    }
  }, []);

  useEffect(() => {
    window._setTrackHeader = setTrackHeader;

    const idleCallback = requestIdleCallback(initGtm);
    return () => {
      cancelIdleCallback(idleCallback);
    };
  }, [initGtm]);

  useEffect(() => {
    window[GTM_DATA_LAYER].push({
      event: 'dataLayerReady',
      eventCallback: setGtmCookieToXTrack,
      eventTimeout: 100,
    });
    log('dataLayerReady');
  }, [setGtmCookieToXTrack]);

  useEffect(() => {
    window[GTM_DATA_LAYER].push({ client_id: clientId });
    log('set gtm clientId', clientId);
  }, [clientId]);

  useEffect(() => {
    window[GTM_DATA_LAYER].push({ 'user.id': userId });
    window[GTM_DATA_LAYER].push({ 'user.id': userId, event: 'user.id.set' });
    log('set gtm user.id', userId);
  }, [userId]);

  useEffect(() => {
    window[GTM_DATA_LAYER].push({ 'user.username': username });
    window[GTM_DATA_LAYER].push({
      'user.username': username,
      event: 'user.username.set',
    });
    log('set gtm user.username', username);
  }, [username]);

  return null;
};

GtmLoader.propTypes = {
  clientId: PropTypes.string,
  userId: PropTypes.string,
  username: PropTypes.string,
};

export default GtmLoader;
